import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Upload, AlertCircle, CheckCircle, Loader2, FileSpreadsheet, Pencil, Play } from 'lucide-react';
import { ImportPreview } from './ImportPreview';
import { ImportEditTable } from './ImportEditTable';
import { runEndpoint } from '@/services/flow';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const STAGES = {
  UPLOAD: 'upload',
  VALIDATING: 'validating',
  PREVIEW: 'preview',
  EDIT: 'edit',
  IMPORTING: 'importing',
  COMPLETE: 'complete'
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['.xlsx', '.xls'];

/**
 * Multi-step wizard dialog for importing data dictionary from Excel
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onOpenChange - Callback when dialog open state changes
 * @param {Function} props.onImportComplete - Callback when import successfully completes
 */
export function DictionaryImportDialog({ open, onOpenChange, onImportComplete }) {
  const { user } = useAuth();
  const [stage, setStage] = useState(STAGES.UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [editableItems, setEditableItems] = useState([]);
  const [pendingOverrides, setPendingOverrides] = useState(null);
  const fileInputRef = useRef(null);
  const editTableRef = useRef(null);

  const resetDialog = () => {
    setStage(STAGES.UPLOAD);
    setSelectedFile(null);
    setUploading(false);
    setValidationResult(null);
    setImportResult(null);
    setError(null);
    setEditableItems([]);
    setPendingOverrides(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const validateFile = (file) => {
    // Check file type
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      return {
        valid: false,
        error: `Invalid file type. Please upload ${ALLOWED_EXTENSIONS.join(' or ')} files only.`
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit.`
      };
    }

    return { valid: true };
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      setSelectedFile(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const uploadFileToS3 = async (file) => {
    try {
      // Generate a date path for the upload
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const datePath = `year=${year}/month=${month}/day=${day}`;

      // Get presigned URL - use 'dictionary-import' as a virtual dataSourceId for imports
      const urlResponse = await runEndpoint('cortex.vdd.s3.get_datasource_signed_url_put', {
        date: datePath,
        dataSourceId: 'dictionary-import',
        filename: file.name
      });

      const presignedUrl = urlResponse.url;
      if (!presignedUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Upload file to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream'
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Return the S3 key/path for the uploaded file
      const s3Key = urlResponse.key || `data-governance/vdd/datasources/dictionary-import/input/${datePath}/${file.name}`;
      return s3Key;
    } catch (err) {
      throw new Error(`Upload failed: ${err.message}`);
    }
  };

  const handleUploadAndValidate = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setStage(STAGES.VALIDATING);

    try {
      // Upload file to S3
      const s3Key = await uploadFileToS3(selectedFile);

      // Run validation endpoint with dry-run mode
      // Don't specify sheetName to use first sheet in the workbook
      const result = await runEndpoint('cortex.vdd.dictionary.import_with_validation', {
        filePath: s3Key,
        dryRun: true,
        validateOnly: false
      });

      // The endpoint returns the result directly, not wrapped in data
      // Even if success is false, we want to show the preview with errors
      setValidationResult(result);
      setStage(STAGES.PREVIEW);

      // Check for validation errors
      if (result.errors && result.errors.length > 0) {
        toast.error(`Validation found ${result.errors.length} error(s). Please review and fix before importing.`);
      } else {
        toast.success(`Ready to import ${(result.preview?.toAdd?.length || 0) + (result.preview?.toUpdate?.length || 0)} elements.`);
      }
    } catch (err) {
      setError(err.message);
      setStage(STAGES.UPLOAD);
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleProceedToEdit = () => {
    // Use all elements parsed from file as the editable source
    const allElements = validationResult?.allElements || [];
    setEditableItems(allElements.map(item => ({ ...item })));
    setStage(STAGES.EDIT);
  };

  const handleImportAsIs = () => handleConfirmImport();

  const handleBackToPreviewWithEdits = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setStage(STAGES.VALIDATING);

    try {
      const s3Key = await uploadFileToS3(selectedFile);

      // Build overrides from edited items
      const overrides = {};
      for (const item of editableItems) {
        if (item._edited) {
          overrides[item.canonical_name] = {
            definition: item.definition,
            data_type: item.data_type,
            reference_list_name: item.reference_list_name,
            completeness_weight: item.completeness_weight,
            timeliness_weight: item.timeliness_weight,
            status: item.status,
            business_summary: item.business_summary,
            processing_language: item.processing_language,
            processing_rules: item.processing_rules,
            confidence_score: item.confidence_score
          };
        }
      }

      const overridesPayload = Object.keys(overrides).length > 0 ? overrides : null;
      setPendingOverrides(overridesPayload);

      // Re-validate with overrides applied
      const result = await runEndpoint('cortex.vdd.dictionary.import_with_validation', {
        filePath: s3Key,
        dryRun: true,
        validateOnly: false,
        overrides: overridesPayload || undefined
      });

      setValidationResult(result);
      setStage(STAGES.PREVIEW);
    } catch (err) {
      setError(err.message);
      setStage(STAGES.EDIT);
      toast.error(err.message || 'Re-validation failed');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setStage(STAGES.IMPORTING);

    try {
      // Upload file to S3 again
      const s3Key = await uploadFileToS3(selectedFile);

      // Run actual import
      const changedBy = (user?.first_name && user?.last_name)
        ? `${user.first_name} ${user.last_name}`
        : user?.email || 'Import';

      const payload = {
        filePath: s3Key,
        dryRun: false,
        validateOnly: false,
        changedBy
      };

      // Include overrides from edit step if any
      if (pendingOverrides) {
        payload.overrides = pendingOverrides;
      }

      const response = await runEndpoint('cortex.vdd.dictionary.import_with_validation', payload);

      // Response structure: { success, dryRun, results: { added, updated, errors } }
      // The endpoint returns results directly, not wrapped in data
      const result = response;
      setImportResult(result);
      setStage(STAGES.COMPLETE);

      const totalImported = (result.results?.added || 0) + (result.results?.updated || 0);
      const importErrors = result.results?.errors || [];

      if (importErrors.length > 0) {
        if (totalImported > 0) {
          toast.warning(`Imported ${totalImported} elements with ${importErrors.length} errors.`);
        } else {
          toast.error(`Import failed with ${importErrors.length} errors.`);
        }
      } else {
        toast.success(`Successfully imported ${totalImported} data elements.`);
      }

      // Notify parent component
      if (onImportComplete) {
        onImportComplete(result);
      }
    } catch (err) {
      setError(err.message);
      setStage(STAGES.PREVIEW);
      toast.error(err.message || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const renderUploadStage = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="file-upload">Select File</Label>
        <Input
          id="file-upload"
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          disabled={uploading}
        />
        {selectedFile && (
          <p className="text-sm text-muted-foreground">
            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
          </p>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderValidatingStage = () => (
    <div className="space-y-6 py-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center">
          <h3 className="font-semibold text-lg">Validating File...</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Please wait while we validate your data dictionary file.
          </p>
        </div>
        <Progress value={undefined} className="w-64" />
      </div>
    </div>
  );

  const renderPreviewStage = () => {
    const hasErrors = validationResult?.errors?.length > 0;
    const canImport = !hasErrors && validationResult?.preview;

    return (
      <div className="space-y-4">
        <Alert className={hasErrors ? '' : 'border-green-200 bg-green-50'}>
          {hasErrors ? (
            <>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Validation found errors that must be fixed before importing.
              </AlertDescription>
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                File validated successfully. Review the changes below, then import as is or edit properties first.
              </AlertDescription>
            </>
          )}
        </Alert>

        <ImportPreview
          preview={validationResult?.preview}
          errors={validationResult?.errors || []}
          warnings={validationResult?.warnings || []}
        />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  };

  const renderEditStage = () => (
    <div className="space-y-4">
      <Alert className="border-blue-200 bg-blue-50">
        <Pencil className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Review and edit element properties before importing. Click the pencil icon on any row to modify.
        </AlertDescription>
      </Alert>

      <ImportEditTable
        ref={editTableRef}
        items={editableItems}
        onChange={setEditableItems}
      />
    </div>
  );

  const renderImportingStage = () => (
    <div className="space-y-6 py-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center">
          <h3 className="font-semibold text-lg">Importing Data...</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Please wait while we import your data dictionary. Do not close this dialog.
          </p>
        </div>
        <Progress value={undefined} className="w-64" />
      </div>
    </div>
  );

  const renderCompleteStage = () => {
    const added = importResult?.results?.added || 0;
    const updated = importResult?.results?.updated || 0;
    const errorsList = importResult?.results?.errors || [];
    const errorsCount = errorsList.length;
    const hasSuccess = added > 0 || updated > 0;

    return (
      <div className="space-y-6 py-4">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className={`h-16 w-16 rounded-full flex items-center justify-center ${errorsCount > 0 && !hasSuccess ? 'bg-red-100' : errorsCount > 0 ? 'bg-yellow-100' : 'bg-green-100'}`}>
            {errorsCount > 0 && !hasSuccess ? (
              <AlertCircle className="h-10 w-10 text-red-600" />
            ) : errorsCount > 0 ? (
              <AlertCircle className="h-10 w-10 text-yellow-600" />
            ) : (
              <CheckCircle className="h-10 w-10 text-green-600" />
            )}
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-xl">
              {errorsCount > 0 && !hasSuccess ? 'Import Failed' : errorsCount > 0 ? 'Import Completed with Errors' : 'Import Complete!'}
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              {errorsCount > 0 && !hasSuccess
                ? 'No elements were imported. Please review the errors below.'
                : errorsCount > 0
                ? 'Some elements were imported, but errors occurred.'
                : 'Your data dictionary has been successfully imported.'}
            </p>
          </div>

          <div className={`grid gap-4 w-full max-w-md mt-6 ${errorsCount > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{added}</div>
              <div className="text-sm text-muted-foreground">Added</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{updated}</div>
              <div className="text-sm text-muted-foreground">Updated</div>
            </div>
            {errorsCount > 0 && (
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{errorsCount}</div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </div>
            )}
          </div>

          {errorsCount > 0 && (
            <div className="w-full mt-4 max-h-60 overflow-y-auto">
              <div className="text-sm font-medium mb-2">Error Details:</div>
              <div className="space-y-2">
                {errorsList.slice(0, 20).map((err, idx) => (
                  <div key={idx} className="text-sm p-2 bg-red-50 border border-red-200 rounded">
                    <span className="font-medium">{err.canonical_name}:</span>{' '}
                    <span className="text-red-700">{err.error?.substring(0, 100)}{err.error?.length > 100 ? '...' : ''}</span>
                  </div>
                ))}
                {errorsList.length > 20 && (
                  <div className="text-sm text-muted-foreground">
                    ... and {errorsList.length - 20} more errors
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (stage) {
      case STAGES.UPLOAD:
        return renderUploadStage();
      case STAGES.VALIDATING:
        return renderValidatingStage();
      case STAGES.PREVIEW:
        return renderPreviewStage();
      case STAGES.EDIT:
        return renderEditStage();
      case STAGES.IMPORTING:
        return renderImportingStage();
      case STAGES.COMPLETE:
        return renderCompleteStage();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    const hasErrors = validationResult?.errors?.length > 0;
    const canImport = !hasErrors && validationResult?.preview;
    const hasChanges = (validationResult?.preview?.toAdd?.length || 0) + (validationResult?.preview?.toUpdate?.length || 0) > 0;

    switch (stage) {
      case STAGES.UPLOAD:
        return (
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadAndValidate}
              disabled={!selectedFile || uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload & Validate
            </Button>
          </>
        );
      case STAGES.VALIDATING:
        return null;
      case STAGES.PREVIEW:
        return (
          <>
            <Button variant="outline" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleProceedToEdit}
              disabled={!canImport || uploading}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit Before Import
            </Button>
            <Button
              onClick={handleImportAsIs}
              disabled={!canImport || !hasChanges || uploading}
            >
              <Play className="mr-2 h-4 w-4" />
              Import As Is
            </Button>
          </>
        );
      case STAGES.EDIT:
        return (
          <>
            <Button variant="outline" onClick={() => setStage(STAGES.PREVIEW)}>
              Cancel
            </Button>
            <Button
              onClick={handleBackToPreviewWithEdits}
              disabled={uploading || editableItems.length === 0}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Done Editing
            </Button>
          </>
        );
      case STAGES.IMPORTING:
        return null;
      case STAGES.COMPLETE:
        return (
          <Button onClick={handleClose}>
            Close
          </Button>
        );
      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    switch (stage) {
      case STAGES.UPLOAD:
        return 'Import Data Dictionary';
      case STAGES.VALIDATING:
        return 'Validating File';
      case STAGES.PREVIEW:
        return 'Review Import';
      case STAGES.EDIT:
        return 'Edit Properties';
      case STAGES.IMPORTING:
        return 'Importing Data';
      case STAGES.COMPLETE:
        return 'Import Complete';
      default:
        return 'Import Data Dictionary';
    }
  };

  const getDialogDescription = () => {
    switch (stage) {
      case STAGES.UPLOAD:
        return 'Upload an Excel file to import data elements into your dictionary.';
      case STAGES.PREVIEW:
        return 'Review the changes that will be made to your data dictionary.';
      case STAGES.EDIT:
        return 'Edit element properties before importing. Click the pencil icon to modify a row.';
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!w-[95vw] !max-w-[1400px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          {getDialogDescription() && (
            <DialogDescription>{getDialogDescription()}</DialogDescription>
          )}
        </DialogHeader>

        <div className="py-4">
          {renderContent()}
        </div>

        <DialogFooter>
          {renderFooter()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
